// src/components/admin/business/PackageForm.jsx
import React, { useState, useEffect, forwardRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { businessService } from '../../../services/business';
import { useNotification } from '../../../context/NotificationContext';
import { FaPlus, FaTimes, FaCheck, FaStar } from 'react-icons/fa';

// ─────────────────────────────────────────────
// Inline UI Components (selaras VillaForm / ServiceForm)
// ─────────────────────────────────────────────

const Button = ({
  children,
  type = 'button',
  variant = 'primary',
  loading = false,
  disabled = false,
  onClick,
  className = '',
  ...props
}) => {
  const baseClass =
    'inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold transition-all duration-200 ' +
    'disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 ' +
    'transform hover:scale-105 active:scale-95 shadow-lg';

  const variants = {
    primary:
      'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white focus:ring-amber-500 shadow-amber-200',
    outline:
      'border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-700 focus:ring-gray-500 shadow-sm',
    danger:
      'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white focus:ring-red-500 shadow-red-200',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClass} ${variants[variant] || variants.primary} ${className}`}
      {...props}
    >
      {loading && (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
      )}
      {children}
    </button>
  );
};

const Input = forwardRef(
  ({ label, error, required = false, className = '', type = 'text', ...props }, ref) => (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-semibold text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <input
        ref={ref}
        type={type}
        className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all shadow-sm ${
          error ? 'border-red-500 focus:ring-red-500' : ''
        } ${className}`}
        {...props}
      />
      {error && (
        <p className="text-sm text-red-600 mt-1 flex items-center">
          <FaTimes className="mr-1" />
          {error}
        </p>
      )}
    </div>
  )
);
Input.displayName = 'Input';

// ─────────────────────────────────────────────
// Section Header
// ─────────────────────────────────────────────
const SectionHeader = ({ icon: Icon = FaCheck, title }) => (
  <div className="flex items-center gap-3 pb-4 border-b-2 border-amber-200">
    <div className="p-2 bg-amber-100 rounded-lg">
      <Icon className="text-amber-600" />
    </div>
    <h3 className="text-xl font-bold text-gray-900">{title}</h3>
  </div>
);

// ─────────────────────────────────────────────
// PackageForm — MAIN
// ─────────────────────────────────────────────
const PackageForm = ({ package: pkg, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      package_type: 'full_management',
      management_fee_percentage: 15,
      features: [''],
      status: 'active',
      featured: false,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'features',
  });

  const watchedValues = watch();

  // ── load data on edit ──
  useEffect(() => {
    if (!pkg) return;

    const formFields = [
      'name',
      'description',
      'package_type',
      'management_fee_percentage',
      'status',
      'featured',
    ];
    formFields.forEach((key) => {
      if (pkg[key] !== undefined) setValue(key, pkg[key]);
    });

    if (pkg.features) {
      let features = [];
      if (typeof pkg.features === 'string') {
        try {
          features = JSON.parse(pkg.features);
        } catch (e) {
          features = [pkg.features];
        }
      } else if (Array.isArray(pkg.features)) {
        features = pkg.features;
      }
      setValue('features', features.length > 0 ? features : ['']);
    }
  }, [pkg, setValue]);

  // ── submit ──
  const onSubmit = async (data) => {
    const filteredFeatures = data.features.filter(
      (feature) => feature && feature.trim() !== ''
    );

    if (filteredFeatures.length === 0) {
      console.error('At least one feature is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: data.name.trim(),
        description: data.description.trim(),
        package_type: data.package_type,
        management_fee_percentage: parseFloat(data.management_fee_percentage),
        features: filteredFeatures,
        status: data.status,
        featured: Boolean(data.featured),
      };

      if (pkg) {
        await businessService.update(pkg.id, payload);
        // REMOVED: addNotification toast
      } else {
        await businessService.create(payload);
        // REMOVED: addNotification toast
      }

      // Just call parent's onSuccess which will show SweetAlert
      onSuccess();
    } catch (error) {
      console.error('Error saving package:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'An error occurred while saving the package';
      
      // REMOVED: addNotification error toast
      console.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // ── helpers ──
  const packageTypeOptions = [
    { value: 'full_management', label: 'Full Management' },
    { value: 'marketing',       label: 'Marketing & Booking' },
    { value: 'operational',     label: 'Operational' },
  ];

  const statusOptions = [
    { value: 'active',   label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  const renderSelect = (label, name, options, required = false) => (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        {...register(name, required ? { required: `${label} is required` } : {})}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white text-gray-900 transition-all shadow-sm hover:border-amber-400"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {errors[name] && (
        <p className="text-sm text-red-600 mt-1 flex items-center">
          <FaTimes className="mr-1" />
          {errors[name].message}
        </p>
      )}
    </div>
  );

  const canSubmit = () => {
    const hasRequired =
      watchedValues.name?.trim() &&
      watchedValues.description?.trim() &&
      watchedValues.package_type &&
      watchedValues.management_fee_percentage !== '' &&
      watchedValues.features?.some((f) => f && f.trim() !== '');
    return hasRequired && !loading;
  };

  // ── render ──
  return (
    <div className="max-w-5xl mx-auto p-8 bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

        {/* ═══════════ 1. Basic Information ═══════════ */}
        <div className="space-y-6">
          <SectionHeader title="Basic Information" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Package Name */}
            <div className="md:col-span-2">
              <Input
                label="Package Name"
                required
                {...register('name', {
                  required: 'Package name is required',
                  maxLength: { value: 100, message: 'Package name must not exceed 100 characters' },
                })}
                error={errors.name?.message}
                placeholder="e.g. Premium Management Package"
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Full Description <span className="text-red-500">*</span>
              </label>
              <textarea
                {...register('description', { required: 'Description is required' })}
                rows={4}
                placeholder="Describe the package in detail..."
                className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-vertical shadow-sm transition-all ${
                  errors.description ? 'border-red-500 focus:ring-red-500' : ''
                }`}
              />
              {errors.description && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <FaTimes className="mr-1" />
                  {errors.description.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ═══════════ 2. Package Details ═══════════ */}
        <div className="space-y-6">
          <SectionHeader title="Package Details" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Package Type */}
            {renderSelect('Package Type', 'package_type', packageTypeOptions, true)}

            {/* Management Fee — highlighted card */}
            <div className="space-y-4 p-5 bg-gradient-to-br from-amber-50 to-white rounded-xl border-2 border-amber-400">
              <h4 className="text-base font-bold text-gray-800 flex items-center gap-2">
                Management Fee <span className="text-xs font-normal text-amber-600">(in percentage)</span>
              </h4>
              <Input
                label="Fee (%)"
                type="number"
                required
                step="0.01"
                min="0"
                max="100"
                {...register('management_fee_percentage', {
                  required: 'Management fee is required',
                  min: { value: 0, message: 'Fee must be at least 0%' },
                  max: { value: 100, message: 'Fee must not exceed 100%' },
                  valueAsNumber: true,
                })}
                error={errors.management_fee_percentage?.message}
                placeholder="15.00"
              />
            </div>
          </div>

          {/* Fee info box */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <FaCheck className="text-amber-600 mt-1 flex-shrink-0" />
            <div className="text-sm text-amber-800">
              <p className="font-bold mb-1">Note:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Management fee is a percentage of the revenue from the managed villa.</li>
                <li>A value of <strong>0%</strong> means free, a value of <strong>100%</strong> means all revenue.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ═══════════ 3. Package Features ═══════════ */}
        <div className="space-y-6">
          <SectionHeader title="Package Features" />

          <div>
            <div className="flex justify-between items-center mb-4">
              <label className="block text-sm font-semibold text-gray-700">
                Feature List <span className="text-red-500">*</span>
              </label>
              <Button
                type="button"
                variant="outline"
                onClick={() => append('')}
                className="px-4 py-2 text-sm"
              >
                <FaPlus className="mr-2" />
                Add Feature
              </Button>
            </div>

            <div className="space-y-3 border-2 border-gray-200 rounded-xl p-5 bg-white shadow-inner">
              {fields.map((field, index) => (
                <div key={field.id} className="flex gap-3 items-center">
                  {/* Index number */}
                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <span className="text-sm font-bold text-amber-700">{index + 1}</span>
                  </div>

                  {/* Feature input */}
                  <div className="flex-1">
                    <input
                      {...register(`features.${index}`, {
                        required: index === 0 ? 'At least one feature is required' : false,
                      })}
                      placeholder={`Feature ${index + 1}`}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all shadow-sm ${
                        errors.features?.[index] ? 'border-red-500 focus:ring-red-500' : ''
                      }`}
                    />
                  </div>

                  {/* Remove button */}
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl border-2 border-red-200 text-red-500 hover:bg-red-50 hover:border-red-400 hover:text-red-700 transition-all"
                      title={`Remove feature ${index + 1}`}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {errors.features?.[0] && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <FaTimes className="mr-1" />
                {errors.features[0].message}
              </p>
            )}

            <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
              <FaCheck className="text-amber-600" />
              Add all features included in this package ({fields.length} feature{fields.length !== 1 ? 's' : ''})
            </p>
          </div>
        </div>

        {/* ═══════════ 4. Status & Settings ═══════════ */}
        <div className="space-y-6">
          <SectionHeader title="Status & Settings" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {renderSelect('Status', 'status', statusOptions, true)}

            <div className="flex items-center space-x-3 pt-8">
              <input
                type="checkbox"
                {...register('featured')}
                className="h-5 w-5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded cursor-pointer"
              />
              <label className="text-sm font-medium text-gray-900 select-none cursor-pointer flex items-center gap-2">
                <FaStar className="text-amber-500" />
                Featured Package (Displayed on homepage)
              </label>
            </div>
          </div>
        </div>

        {/* ═══════════ 5. Form Summary ═══════════ */}
        <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-6 shadow-inner">
          <h4 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FaCheck className="text-amber-600" />
            Form Summary:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
            {[
              ['Package Name',    watchedValues.name?.trim()                                                           || 'Not filled'],
              ['Package Type',    packageTypeOptions.find((o) => o.value === watchedValues.package_type)?.label       || 'Not selected'],
              ['Management Fee',  watchedValues.management_fee_percentage !== '' ? `${watchedValues.management_fee_percentage}%` : 'Not filled'],
              ['Status',          statusOptions.find((o) => o.value === watchedValues.status)?.label                   || 'Active'],
              ['Featured',        watchedValues.featured ? 'Yes' : 'No'],
              ['Total Features',  `${watchedValues.features?.filter((f) => f && f.trim() !== '').length || 0} feature${(watchedValues.features?.filter((f) => f && f.trim() !== '').length || 0) !== 1 ? 's' : ''}`],
            ].map(([key, val]) => (
              <div key={key} className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                <span>
                  <strong>{key}:</strong> {val}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ═══════════ Actions ═══════════ */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t-2 border-gray-200">
          {/* Mode badge */}
          <div className="text-sm text-gray-600 font-medium">
            {pkg ? (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-lg">
                <FaCheck className="text-amber-600" />
                Edit Mode: Package ID {pkg.id}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-lg">
                <FaCheck className="text-green-600" />
                Add New Package Mode
              </span>
            )}
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={!canSubmit()}
              className="w-full sm:w-auto"
            >
              {loading ? 'Saving...' : pkg ? 'Update Package' : 'Save Package'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PackageForm;